import React, { useEffect, useState } from 'react';
import { dispatchAlertToaster } from 'adapter/in/common/context/Alert';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { isLocalTextBlockId } from 'adapter/in/dashboards/components/Grid/localTextBlock';
import { mapGridElementsToLayout } from 'adapter/in/dashboards/components/Grid/gridUtils';
import { useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import i18n from 'asset/translation';
import Layout, { LayoutType } from 'domain/entity/layout';
import { ErrorData } from 'domain/types';
import { isLeft } from 'fp-ts/lib/Either';
import { isSome, Option, some } from 'fp-ts/lib/Option';
import { CreateTextBlockCriteriaBuilder } from 'use-case/widgets/create/createTextBlockCriteria';
import type { CreateTextBlockResult } from 'use-case/widgets/create/createTextBlockUseCase';
import UpdateLayoutCriteria, { UpdateLayoutCriteriaBuilder } from 'use-case/layout/update/updateLayoutCriteria';

export type SaveReportTemplateLayoutUseCase = {
    dataFromUpdateTemplateLayout: Option<Layout>;
    execute: (criteria: UpdateLayoutCriteria) => void;
    setLayoutsData?: React.Dispatch<React.SetStateAction<Option<Layout[]>>>;
    reset: () => void;
    error: Option<ErrorData>;
    isLoading?: boolean;
};

type UseSaveReportTemplateLayoutOptions = {
    onSaveSuccess?: () => void;
    exitEditModeOnSave?: boolean;
};

type TextBlockCreationResult = { localId: string; result: CreateTextBlockResult };

export const useSaveReportTemplateLayout = (
    reportTemplateId: string,
    updateTemplateLayout: SaveReportTemplateLayoutUseCase,
    options: UseSaveReportTemplateLayoutOptions = {}
) => {
    const { dataFromUpdateTemplateLayout, execute, reset, setLayoutsData, error, isLoading } = updateTemplateLayout;
    const { onSaveSuccess, exitEditModeOnSave = true } = options;
    const { setEditLayoutMode, setHasLayoutChanges } = dispatchLayoutsView();
    const { allGridLayouts } = useLayoutsView();
    const { createTextBlock } = useTextBlock();
    const { editors } = useTextBlockData();
    const { setErrorAlert } = dispatchAlertToaster();
    const [isSavingTextBlocks, setIsSavingTextBlocks] = useState(false);

    const getLocalTextBlockHtml = (textBlockId: string): string =>
        editors?.find((editorWithId) => editorWithId.textBlockId === textBlockId)?.editor?.getHTML() ?? '';

    const saveLayout = (layout: Layout) => {
        execute(UpdateLayoutCriteriaBuilder.create().withLayout(layout).withDashboardId(reportTemplateId).build());
        setLayoutsData?.(some([layout]));
    };

    const saveLayoutWithPersistedTextBlocks = async (layoutElements: GridLayoutElement[]) => {
        if (!createTextBlock) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            return;
        }

        setIsSavingTextBlocks(true);

        const createLocalTextBlock = (localTextBlock: GridLayoutElement): Promise<TextBlockCreationResult> =>
            createTextBlock(
                CreateTextBlockCriteriaBuilder.create()
                    .withDashboardId(reportTemplateId)
                    .withRichText(getLocalTextBlockHtml(localTextBlock.i))
                    .build()
            ).then((result) => ({
                localId: localTextBlock.i,
                result,
            }));

        const createLocalTextBlocksSequentially = (localTextBlocks: GridLayoutElement[]): Promise<TextBlockCreationResult[]> => {
            const [firstTextBlock, ...remainingTextBlocks] = localTextBlocks;
            if (!firstTextBlock) return Promise.resolve([]);

            return remainingTextBlocks.reduce<Promise<TextBlockCreationResult[]>>(
                (previousResultsPromise, localTextBlock) =>
                    previousResultsPromise.then((previousResults) =>
                        createLocalTextBlock(localTextBlock).then((textBlockCreationResult) => [
                            ...previousResults,
                            textBlockCreationResult,
                        ])
                    ),
                createLocalTextBlock(firstTextBlock).then((textBlockCreationResult) => [textBlockCreationResult])
            );
        };

        const localTextBlocks = layoutElements.filter((element) => isLocalTextBlockId(element.i));
        const textBlockCreationResults = await createLocalTextBlocksSequentially(localTextBlocks);

        if (textBlockCreationResults.some(({ result }) => isLeft(result))) {
            setErrorAlert(i18n.t('reports.reportTemplate.layoutSaveError'));
            setIsSavingTextBlocks(false);
            return;
        }

        const createdTextBlockIdsByLocalId = new Map<string, string>();
        textBlockCreationResults.forEach(({ localId, result }) => {
            if (!isLeft(result)) {
                createdTextBlockIdsByLocalId.set(localId, result.right.id);
            }
        });

        const persistedLayoutElements = layoutElements.map((element) => ({
            ...element,
            i: createdTextBlockIdsByLocalId.get(element.i) ?? element.i,
        }));

        saveLayout(mapGridElementsToLayout(persistedLayoutElements, LayoutType.A4));
        setIsSavingTextBlocks(false);
    };

    const handleUpdateLayoutAction = () => {
        const gridLayoutsData = allGridLayouts[LayoutType.A4];

        if (!gridLayoutsData) return;

        const layoutElements = gridLayoutsData.filter((element) => !isDashboardFrame(element.i, element.type));
        const localTextBlocks = layoutElements.filter((element) => isLocalTextBlockId(element.i));

        if (localTextBlocks.length === 0) {
            saveLayout(mapGridElementsToLayout(layoutElements, LayoutType.A4));
            return;
        }

        saveLayoutWithPersistedTextBlocks(layoutElements);
    };

    useEffect(() => {
        if (isSome(dataFromUpdateTemplateLayout)) {
            if (exitEditModeOnSave) {
                setEditLayoutMode(false);
            }
            setHasLayoutChanges(false);
            reset();
            onSaveSuccess?.();
        }
    }, [dataFromUpdateTemplateLayout]);

    useUseCaseAlerts(dataFromUpdateTemplateLayout, error, {
        successMessage: 'reports.reportTemplate.layoutSaveSuccess',
        errorMessage: 'reports.reportTemplate.layoutSaveError',
    });

    return {
        handleUpdateLayoutAction,
        isSaving: isSavingTextBlocks || !!isLoading,
        hasError: isSome(error),
    };
};
